//#region Util Imports

import * as $ from 'jquery'

//#endregion

import { OpUnit } from './op-unit'
import { OpMeter } from './op-meter'
import { OpLocation } from './op-location'
import { OpDevice } from './op-device'
import { OpDataType } from './op-data-type'
import { OpAlarmDefGroup } from './op-alarm-def-group'
import { OpAlarmType } from './op-alarm-type'
import { OpAlarmText } from './op-alarm-text'
import { Enums } from '../common/enums'
import { DataProvider } from '../../helpers/data-provider'
import { IReferenceType, IOpChangeState, IJsonSerializable, ICopyable } from '../util/interfaces'
import { TypeHelper } from '../../helpers'

export class OpAlarmDef
    implements IReferenceType, IOpChangeState, IJsonSerializable, ICopyable<OpAlarmDef>
{
    // #region Properties

    public idAlarmDef: number
    public name: string
    public serialNbr: number
    public idMeter: number
    public idLocation: number
    public idAlarmType: number
    public idDataTypeAlarm: number
    public idDataTypeConfig: number
    public systemAlarmValue: any
    public isEnabled: boolean
    public idAlarmText: number
    public idAlarmData: number
    public checkLastAlarm: boolean
    public suspensionTime: number
    public isConfirmable: boolean
    public indexNbr: number

    // #endregion

    // #region Navigation Properties

    public device: OpDevice
    public meter: OpMeter
    public location: OpLocation
    public alarmType: OpAlarmType
    public dataTypeAlarm: OpDataType
    public dataTypeConfig: OpDataType
    public alarmText: OpAlarmText

    // #endregion

    // #region Custom Properties

    public visible: boolean
    public readOnly: boolean
    public orderNbr: number

    public currentAlarmDefGroups: OpAlarmDefGroup[]

    public unit: OpUnit

    // #endregion

    // #region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpAlarmDef) {
            this.copy(<OpAlarmDef>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    // #endregion

    // #region IReferenceType

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdAlarmDef
    }

    public getReferenceKey(): number {
        return this.idAlarmDef
    }

    public getReferenceValue(): any {
        return this
    }

    // #endregion

    // #region IOpDynamic

    public dynamicValues: { Key: string, Value: any }[] = []

    // #endregion

    // #region IOpChangeState

    public opState: Enums.OpChangeState

    // #endregion

    // #region IJsonSerializable

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj != null) {
            this.idAlarmDef = opObj.ID_ALARM_DEF
            this.name = opObj.NAME
            this.serialNbr = opObj.SERIAL_NBR
            this.idMeter = opObj.ID_METER
            this.idLocation = opObj.ID_LOCATION
            this.idAlarmType = opObj.ID_ALARM_TYPE
            this.idDataTypeAlarm = opObj.ID_DATA_TYPE_ALARM
            this.idDataTypeConfig = opObj.ID_DATA_TYPE_CONFIG
            this.systemAlarmValue = opObj.SYSTEM_ALARM_VALUE
            this.isEnabled = opObj.IS_ENABLED
            this.idAlarmText = opObj.ID_ALARM_TEXT
            this.idAlarmData = opObj.ID_ALARM_DATA
            this.checkLastAlarm = opObj.CHECK_LAST_ALARM
            this.suspensionTime = opObj.SUSPENSION_TIME
            this.isConfirmable = opObj.IS_CONFIRMABLE
            this.indexNbr = opObj.INDEX_NBR

            this.device = opObj._Device == null ? null : new OpDevice(opObj._Device)
            this.meter = opObj._Meter == null ? null : new OpMeter(opObj._Meter)
            this.location = opObj._Location == null ? null : new OpLocation(opObj._Location)
            this.alarmType = opObj._AlarmType == null ? null : new OpAlarmType(opObj._AlarmType)
            this.dataTypeAlarm = opObj._DataTypeAlarm == null ? null : new OpDataType(opObj._DataTypeAlarm)
            this.dataTypeConfig = opObj._DataTypeConfig == null ? null : new OpDataType(opObj._DataTypeConfig)
            this.alarmText = opObj._AlarmText == null ? null : new OpAlarmText(opObj._AlarmText)

            this.opState = opObj.OpState
            this.visible = opObj.Visible
            this.readOnly = opObj.ReadOnly
            this.orderNbr = opObj.OrderNbr

            this.currentAlarmDefGroups = opObj.CurrentAlarmDefGroups == null ? null : opObj.CurrentAlarmDefGroups.map((obj: any) => new OpAlarmDefGroup(obj))

            this.dynamicValues = opObj.dynamicValues
        } else {
            this.idAlarmDef = 0
            this.opState = Enums.OpChangeState.New
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_ALARM_DEF: this.idAlarmDef,
            NAME: this.name,
            SERIAL_NBR: this.serialNbr,
            ID_METER: this.idMeter,
            ID_LOCATION: this.idLocation,
            ID_ALARM_TYPE: this.idAlarmType,
            ID_DATA_TYPE_ALARM: this.idDataTypeAlarm,
            ID_DATA_TYPE_CONFIG: this.idDataTypeConfig,
            SYSTEM_ALARM_VALUE: this.systemAlarmValue,
            IS_ENABLED: this.isEnabled,
            ID_ALARM_TEXT: this.idAlarmText,
            ID_ALARM_DATA: this.idAlarmData,
            CHECK_LAST_ALARM: this.checkLastAlarm,
            SUSPENSION_TIME: this.suspensionTime,
            IS_CONFIRMABLE: this.isConfirmable,
            INDEX_NBR: this.indexNbr,

            _Device: this.device == null ? null : this.device.toJSON(),
            _Meter: this.meter == null ? null : this.meter.toJSON(),
            _Location: this.location == null ? null : this.location.toJSON(),
            _AlarmType: this.alarmType == null ? null : this.alarmType.toJSON(),
            _DataTypeAlarm: this.dataTypeAlarm == null ? null : this.dataTypeAlarm.toJSON(),
            _DataTypeConfig: this.dataTypeConfig == null ? null : this.dataTypeConfig.toJSON(),
            _AlarmText: this.alarmText == null ? null : this.alarmText.toJSON(),

            OpState: this.opState,
            Visible: this.visible,
            ReadOnly: this.readOnly,
            OrderNbr: this.orderNbr,

            CurrentAlarmDefGroups: this.currentAlarmDefGroups == null ? null : this.currentAlarmDefGroups.map((group: OpAlarmDefGroup) => group.toJSON()),

            dynamicValues: this.dynamicValues
        }
    }

    // #endregion

    // #region ICopyable

    public clone(includeNavigationProperties: boolean, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpAlarmDef {
        const opAlarmDef: OpAlarmDef = new OpAlarmDef(null)

        opAlarmDef.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return opAlarmDef
    }

    public copy(source: OpAlarmDef, includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): void {

        if (source == null) {
            return null
        }

        if (includeNavigationProperties) {
            this.device = source.device
            this.meter = source.meter
            this.location = source.location
            this.alarmType = source.alarmType
            //this.device = source.device == null ? null : source.device.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            //this.meter = source.meter == null ? null : source.meter.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            //this.location = source.location == null ? null : source.location.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            //this.alarmType = source.alarmType == null ? null : source.alarmType.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

            this.dataTypeAlarm = source.dataTypeAlarm == null ? null : source.dataTypeAlarm.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            this.dataTypeConfig = source.dataTypeConfig == null ? null : source.dataTypeConfig.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

            this.alarmText = source.alarmText
            //this.alarmText = source.alarmText == null ? null : source.alarmText.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeCustomProperties) {
            this.visible = source.visible
            this.readOnly = source.readOnly
            this.orderNbr = source.orderNbr

            this.currentAlarmDefGroups = source.currentAlarmDefGroups == null
                ? null
                : source.currentAlarmDefGroups.map((group: OpAlarmDefGroup) => group.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
        }

        if (includeDataList) {

        }

        if (includeDynamicValues) {
            this.dynamicValues = TypeHelper.clone(source.dynamicValues, true)
        }

        this.idAlarmDef = source.idAlarmDef
        this.name = source.name
        this.serialNbr = source.serialNbr
        this.idMeter = source.idMeter
        this.idLocation = source.idLocation
        this.idAlarmType = source.idAlarmType
        this.idDataTypeAlarm = source.idDataTypeAlarm
        this.idDataTypeConfig = source.idDataTypeConfig
        this.systemAlarmValue = source.systemAlarmValue
        this.isEnabled = source.isEnabled
        this.idAlarmText = source.idAlarmText
        this.idAlarmData = source.idAlarmData
        this.checkLastAlarm = source.checkLastAlarm
        this.suspensionTime = source.suspensionTime
        this.isConfirmable = source.isConfirmable
        this.indexNbr = source.indexNbr

        this.opState = source.opState
    }

    // #endregion

    public toString(): string {
        return this.name
    }

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpAlarmDef[]): JQueryPromise<OpAlarmDef[]> {
        const defered: JQueryDeferred<OpAlarmDef[]> = $.Deferred<OpAlarmDef[]>()

        defered.resolve(list)

        return defered.promise()
    }
}
